/**
 * Recuperación de contraseña (contrato §1).
 *
 * `POST /api/auth/forgot-password` responde **204 siempre**, exista el email o no: un 404
 * acá enumeraría los usuarios del club. La pantalla dice exactamente eso, para que nadie
 * lea el mensaje neutro como una falla.
 *
 * Lo que llega al correo es un **código** para pegar en «Nueva contraseña», no un enlace:
 * los textos lo dicen así y la pantalla de «revisá tu correo» lleva directo a pegarlo.
 */
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { Mail } from "lucide-react";
import { ApiError, http } from "../../lib/api.js";
import { Button } from "../../components/ui/Button.js";
import { Input } from "../../components/ui/Input.js";
import { AuthShell } from "./AuthShell.js";

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await http.post("/api/auth/forgot-password", { email: email.trim() }, { anonymous: true });
      setSent(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "No se pudo enviar el pedido.");
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <AuthShell
        kicker="Recuperación"
        title="Revisá tu correo"
        description="Si ese email corresponde a un usuario activo, le mandamos un código para restablecer la contraseña. Vence en una hora y sirve una sola vez."
        footer={
          <Link to="/login" className="transition-colors hover:text-ink">
            Volver al login
          </Link>
        }
      >
        <div className="flex flex-col gap-4">
          <Button variant="primary" block onClick={() => navigate("/nueva-contrasena")}>
            Ya tengo el código
          </Button>
          <p className="text-[12px] leading-relaxed text-dim">
            La respuesta es la misma exista o no el email: mostrar la diferencia permitiría
            averiguar quiénes tienen usuario.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      kicker="Recuperación"
      title="Restablecer contraseña"
      description="Te mandamos un código al correo con el que entrás al sistema."
      footer={
        <Link to="/login" className="transition-colors hover:text-ink">
          Volver al login
        </Link>
      }
    >
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4">
        <Input
          label="Email"
          type="email"
          required
          autoFocus
          icon={<Mail />}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="vos@viva.local"
        />
        {error ? (
          <p role="alert" className="text-[12px] text-danger-fg">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="primary" block loading={loading}>
          Mandarme el código
        </Button>
      </form>
    </AuthShell>
  );
}

export default ForgotPasswordPage;
